import type { Schema } from "./api";

type Row = Schema["DesignerRow"];
type Round = Schema["DesignerRoundOut"];
type Check = Schema["DesignerCheckOut"];

export type DesignerTopic = "compare" | "link" | "data" | "ready" | "result";

export type DesignerRowAttention = {
  topic: DesignerTopic;
  title: string;
  action: string;
  explanation: string;
  tone: "amber" | "blue" | "green" | "neutral";
};

export const designerTopicLabels: Record<DesignerTopic, string> = {
  compare: "Сверить значения",
  link: "Сопоставить с работой",
  data: "Уточнить данные",
  ready: "Выполнить сверку",
  result: "Результат сверки",
};

/** Explain the row's saved state, never infer it from notes, matching or decisions. */
export function designerRowAttention(row: Row): DesignerRowAttention {
  switch (row.state) {
    case "difference":
      return {
        topic: "compare", title: "Есть расхождение", action: "Сверить значения", tone: "amber",
        explanation: "Сопоставьте ВОР и источники — различаться могут и сами источники.",
      };
    case "unlinked":
      return {
        topic: "link", title: "Позиция не сопоставлена", action: "Сопоставить с работой", tone: "blue",
        explanation: "Проверьте связь с работой: одного совпадения названия недостаточно.",
      };
    case "incomplete":
      if (!row.position_key.trim() && row.work_id?.trim()) {
        return {
          topic: "data", title: "Работа без позиции ВОР", action: "Проверить полноту ВОР", tone: "amber",
          explanation: "В выбранной области есть работа без позиции ВОР; добавление позиции не выполняется автоматически.",
        };
      }
      return {
        topic: "data", title: "Не хватает данных", action: "Проверить основания", tone: "amber",
        explanation: "Даже при одинаковых числах основания могут требовать проверки. Ограничения — внутри.",
      };
    case "units":
      return {
        topic: "data", title: "Единицы требуют уточнения", action: "Уточнить единицы", tone: "amber",
        explanation: "Проверьте единицы по источникам; автоматический пересчёт здесь не выполняется.",
      };
    case "malformed":
      return {
        topic: "data", title: "Строка требует проверки", action: "Проверить исходную строку", tone: "amber",
        explanation: "Сверьте поля строки и замечания чтения с исходной ВОР.",
      };
    case "unparsed":
      return {
        topic: "data", title: "Область не прочитана", action: "Проверить непрочитанную область", tone: "amber",
        explanation: "Проверьте непрочитанную область: отсутствие данных не означает нулевой объём.",
      };
    case "uncertain":
      return {
        topic: "data", title: "Строка прочитана с ограничениями", action: "Проверить исходную строку", tone: "amber",
        explanation: "Сверьте значения с исходником — точность чтения не подтверждена.",
      };
    case "linked":
      return {
        topic: "ready", title: "Готово к сверке", action: "Выполнить сверку", tone: "blue",
        explanation: "Связь установлена, но результата сверки этой строки ещё нет.",
      };
    case "matched":
      return {
        topic: "result", title: "Значения совпадают", action: "Посмотреть результат", tone: "green",
        explanation: "Значения строки совпали; это не подтверждение проверки всего проекта.",
      };
    case "within":
      return {
        topic: "result", title: "В пределах допуска", action: "Посмотреть результат", tone: "green",
        explanation: "Отклонение в пределах допуска; это не означает совпадение значений или проверку всего проекта.",
      };
    default:
      return {
        topic: "data", title: "Состояние требует уточнения", action: "Проверить состояние строки", tone: "neutral",
        explanation: "Для этой строки получено неизвестное состояние. Сохранённые сведения показаны без дополнительной интерпретации.",
      };
  }
}

/** An explicit absent historical check must not silently display the latest snapshot. */
export function designerRows(record: Round, check?: Check | null): Row[] {
  const snapshot = arguments.length < 2 ? record.latest_check : check;
  if (snapshot) return [...snapshot.rows];

  return record.positions.filter(position => position.state !== "heading").map(position => ({
    ...position,
    key: position.position_key,
    notes: [...position.issues],
    scope: "",
    comparisons: [],
    state: position.state !== "ok" ? position.state : record.links[position.position_key] ? "linked" : "unlinked",
    work_id: record.links[position.position_key]?.work_id ?? null,
    decision: null,
    vor_source: null,
  }));
}
